import { memo, useEffect } from 'react';

import { is3pProductKey, type ProductKeys } from '../../../common/constants/products';
import { DEFAULT_BULK_CONNECT_BANNER_STATE } from '../../../common/utils/bulk-connect-banner';
import { serve3PBulkConnectWithoutExposure } from '../../../common/utils/experiments';
import { useBulkConnectLocalStorage } from '../../../common/utils/use-bulk-connect-local-storage';
import { isProductKeyWithTimestampMap } from '../index';

type UpdateBulkConnectLocalStorageProps = {
	accessibleProducts: ProductKeys[] | undefined;
	isConfigLoaded: boolean;
};

export const UpdateBulkConnectLocalStorage: import("react").MemoExoticComponent<({ accessibleProducts, isConfigLoaded }: UpdateBulkConnectLocalStorageProps) => null> = memo(
	({ accessibleProducts, isConfigLoaded }: UpdateBulkConnectLocalStorageProps) => {
		const [localStorageState, setLocalStorageState] = useBulkConnectLocalStorage();

		useEffect(() => {
			if (!isConfigLoaded) {
				return;
			}

			if (!serve3PBulkConnectWithoutExposure()) {
				return;
			}

			const thirdPartyProductKeys =
				accessibleProducts?.filter((product) => is3pProductKey(product)) || [];

			const currentTime = Date.now().toString();
			let hasNewProducts = false;

			const newState = {
				...localStorageState,
				previous3pProducts: localStorageState?.previous3pProducts ?? {},
			};
			// Compare with previous state and store newly visible products with current timestamp, while keeping the previous timestamps for existing products
			const previous3pProducts = isProductKeyWithTimestampMap(localStorageState.previous3pProducts)
				? localStorageState.previous3pProducts
				: {};

			thirdPartyProductKeys.forEach((product) => {
				if (is3pProductKey(product) && !previous3pProducts[product]) {
					newState.previous3pProducts[product] = currentTime;
					hasNewProducts = true;
				}
			});

			if (hasNewProducts) {
				// if there are new products, we need to reset the secondary banner dismissal state
				newState.bulkConnectBanner = {
					...(localStorageState?.bulkConnectBanner || DEFAULT_BULK_CONNECT_BANNER_STATE),
					secondaryBannerDismissed: false,
				};
				setLocalStorageState(newState);
			}
		}, [accessibleProducts, isConfigLoaded, localStorageState, setLocalStorageState]);

		return null;
	},
);
